import { skipToken } from "@reduxjs/toolkit/query";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { SearchInputStyle } from "@repo/ui/Inputs/searchInputStyle";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconSearch } from "@tabler/icons-react";
import { type FC, useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Input,
  ListBox,
  ListBoxItem,
  RootMenuTriggerStateContext,
  SearchField,
} from "react-aria-components";
import { useCustomFieldFilterValuesQuery } from "../../api/entities/userCustomField.js";
import { buildPlaybookScope } from "./buildFilterQueryArgs.js";
import { FilterContext } from "./filterContext.js";

const SEARCH_DEBOUNCE_MS = 250;

export interface TextCustomFieldConditionMenuProps {
  customFieldDefinitionId: string;
  customFieldDisplayName: string;
  playbookId?: PlaybookId;
  onSelect: (condition: CustomFieldCondition) => void;
}

export const TextCustomFieldConditionMenu: FC<
  TextCustomFieldConditionMenuProps
> = (props) => {
  const rootMenuTriggerState = useContext(RootMenuTriggerStateContext);
  const filterContext = useContext(FilterContext);
  const now = useNow();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [search]);

  // Values come from the same ClickHouse data the resulting filter runs
  // against, scoped to the page's playbook and selected date range, so the
  // picker only lists values the filter can actually match.
  const scope = useMemo(
    () =>
      props.playbookId
        ? buildPlaybookScope(
            now,
            props.playbookId,
            filterContext.values.dateRange,
            filterContext.values.dateBasis,
          )
        : undefined,
    [
      now,
      props.playbookId,
      filterContext.values.dateRange,
      filterContext.values.dateBasis,
    ],
  );

  const trimmedSearch = search.trim();
  const valuesQuery = useCustomFieldFilterValuesQuery(
    scope
      ? {
          customFieldDefinitionId: props.customFieldDefinitionId,
          scope,
          query: trimmedSearch ? debouncedSearch || undefined : undefined,
        }
      : skipToken,
  );
  const values = filterValues(valuesQuery.data?.values ?? [], trimmedSearch);
  const items = values.map((value) => ({ value }));

  const handleSelect = (value: string) => {
    props.onSelect({
      customFieldDefinitionId: props.customFieldDefinitionId,
      operator: "=",
      value,
    });

    rootMenuTriggerState?.close();
  };

  return (
    <div className="w-80 overflow-hidden">
      <SearchInputStyle />
      <Autocomplete
        inputValue={search}
        onInputChange={setSearch}
        disableAutoFocusFirst
      >
        <div className="flex items-center gap-2 border-b border-border-light px-3 py-2">
          <IconSearch className="size-4 shrink-0 text-muted-foreground" />
          <SearchField
            aria-label={`Search ${props.customFieldDisplayName} values`}
            className="min-w-0 flex-1"
          >
            <Input
              maxLength={255}
              placeholder="Search values..."
              className="w-full border-0 bg-transparent p-0 text-sm text-foreground shadow-none ring-0 outline-hidden placeholder:text-muted-foreground"
            />
          </SearchField>
          {valuesQuery.isFetching ? (
            <Loader style={LoaderStyle.SPINNER} className="size-3.5 shrink-0" />
          ) : null}
        </div>

        <ListBox
          items={items}
          aria-label={`${props.customFieldDisplayName} values`}
          className="max-h-72 overflow-y-auto p-1 outline-hidden"
          renderEmptyState={() => (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {valuesQuery.isFetching
                ? "Loading values..."
                : valuesQuery.isError
                  ? "Values could not be loaded"
                  : search
                    ? "No matching values"
                    : "No values available"}
            </div>
          )}
        >
          {(option) => (
            <ListBoxItem
              id={option.value}
              textValue={option.value}
              className={menuStyle().item()}
              onAction={() => handleSelect(option.value)}
            >
              <span className="min-w-0 flex-1 truncate">{option.value}</span>
            </ListBoxItem>
          )}
        </ListBox>

        {valuesQuery.data?.hasMore ? (
          <div className="border-t border-border-light px-3 py-2 text-xs text-muted-foreground">
            {search
              ? "Keep typing to narrow the results"
              : "Type to search all values"}
          </div>
        ) : null}
      </Autocomplete>
    </div>
  );
};

export function filterValues(
  values: readonly string[],
  search: string,
): string[] {
  if (!search) return [...values];

  const normalizedSearch = search.toLocaleLowerCase();
  return values.filter((value) =>
    value.toLocaleLowerCase().includes(normalizedSearch),
  );
}
